// React import not needed with modern JSX transform
import Tooltip, { TooltipProps } from '@mui/material/Tooltip';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { styled } from '@mui/material/styles';

// Create a custom styled Tooltip component that includes mobile-specific styles
const StyledTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  zIndex: 9999, // Ensure tooltip is displayed above other elements
  [`& .MuiTooltip-tooltip`]: {
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
    boxShadow: theme.shadows[1],
    border: `1px solid ${theme.palette.divider}`,
    padding: '8px 12px',
    fontSize: '0.75rem',
    maxWidth: '500px',
    wordWrap: 'break-word',
    whiteSpace: 'pre-line',
    margin: '8px 0', // Add margin to ensure spacing from the element
    [theme.breakpoints.down('sm')]: {
      maxWidth: '90vw', // Wider on mobile but capped at 90% of view width
      whiteSpace: 'normal',
      fontSize: '0.75rem',
      padding: '10px 12px',
      margin: '10px 0', // More margin on mobile
      overflowWrap: 'break-word',
      wordBreak: 'break-word',
      boxSizing: 'border-box',
    },
  },
  [`& .MuiTooltip-arrow`]: {
    color: theme.palette.background.paper,
    '&:before': {
      border: `1px solid ${theme.palette.divider}`,
    },
  },
}));

const ResponsiveTooltip = (props: TooltipProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // For mobile devices, we want more specific control over the tooltip positioning
  const mobilePopperProps = {
    modifiers: [
      {
        name: 'preventOverflow',
        enabled: true,
        options: { 
          boundary: 'viewport', 
          padding: 8,
          altAxis: true,
        },
      },
      // Allow flipping between top and bottom to find best position
      { name: 'flip', 
        enabled: true,
        options: {
          fallbackPlacements: ['top', 'bottom'],
        },
       },
      // Add significant offset to ensure it doesn't cover the element
      {
        name: 'offset',
        options: { offset: [0, 8] },
      }
    ],
  };

  // Determine default placement based on device
  const defaultPlacement = isMobile ? 'top' : (props.placement || 'bottom');

  return (
    <StyledTooltip
      arrow={true}
      placement={defaultPlacement}
      PopperProps={{
        ...props.PopperProps,
        ...(isMobile ? mobilePopperProps : {}),
      }}
      enterTouchDelay={0} // Show immediately on mobile touch
      leaveTouchDelay={3000} // Stay visible longer on mobile
      componentsProps={{
        tooltip: {
          sx: { 
            position: 'relative',
            zIndex: 9999, // Ensure tooltip is above other content
          }
        }
      }}
      {...props}
    />
  );
};

export default ResponsiveTooltip;
